import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, ApiError } from "@/api/client";
import type { Service, Staff } from "@/api/types";
import { useStore } from "@/store/StoreContext";
import { Badge, Button, Drawer, Field, Input, tx } from "@/components/ui";
import { OrphansPanel } from "@/components/OrphansPanel";

const DAYS = ["Δευ", "Τρί", "Τετ", "Πέμ", "Παρ", "Σάβ", "Κυρ"];
type DayRow = { on: boolean; start: string; end: string };

/** Staff in one table, like WOW: name, phone chip, status, assigned services, edit / deactivate; archived below. */
export function StaffPage() {
  const { store } = useStore();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Staff | "new" | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const staff = useQuery({ queryKey: ["staff", store?.id], queryFn: () => api<Staff[]>(`/app/staff?store_id=${store!.id}`), enabled: !!store });
  const services = useQuery({ queryKey: ["services", store?.id], queryFn: () => api<Service[]>(`/app/services?store_id=${store!.id}`), enabled: !!store });
  const toggle = useMutation({ mutationFn: ({ id, active }: { id: string; active: boolean }) => api(`/app/staff/${id}`, { method: "PATCH", body: JSON.stringify({ active }) }), onSuccess: () => { qc.invalidateQueries({ queryKey: ["staff"] }); qc.invalidateQueries({ queryKey: ["orphans"] }); } });
  if (!store) return null;
  const active = (staff.data ?? []).filter((s) => s.active);
  const inactive = (staff.data ?? []).filter((s) => !s.active);
  const Row = ({ s }: { s: Staff }) => (
    <tr className={`cursor-pointer border-t hover:bg-gray-50 ${!s.active ? "opacity-70" : ""}`} onClick={() => setEditing(s)}>
      <td className="p-2"><span className="mr-2 inline-block h-2.5 w-2.5 rounded-full align-middle" style={{ background: s.color }} /><span className="font-medium">{s.display_name}</span>{s.phone && <span className="ml-2 rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs text-blue-700">📱 {s.phone}</span>}</td>
      <td className="p-2"><Badge tone={s.active ? "green" : "gray"}>{s.active ? t("staff.active") : t("staff.inactive")}</Badge></td>
      <td className="p-2"><span className="rounded border px-2 py-0.5 text-xs">{s.service_ids.length}</span> <span className="text-xs text-gray-500">{t("nav.services")}</span></td>
      <td className="p-2 text-xs text-gray-500">{s.shifts.filter((sh) => !sh.week_start).length ? t("staff.templateDays", { n: new Set(s.shifts.filter((sh) => !sh.week_start).map((sh) => sh.weekday)).size }) : t("staff.noShifts")}</td>
      <td className="p-2 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="mr-2 text-gray-500 hover:text-gray-800" onClick={() => setEditing(s)} title={t("app.edit")}>✎</button>
        <Link className="mr-2 text-xs underline" to={`/app/shifts?staff=${s.id}`}>{t("nav.shifts")}</Link>
        <button type="button" className="text-xs underline" onClick={() => toggle.mutate({ id: s.id, active: !s.active })}>{s.active ? t("staff.deactivate") : t("staff.activate")}</button>
      </td>
    </tr>
  );
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div><h1 className="text-2xl font-semibold">{t("nav.staff")}</h1><p className="text-sm text-gray-500">{t("staff.subtitle")}</p></div>
        <Button onClick={() => setEditing("new")}>+ {t("staff.new")}</Button>
      </div>
      <OrphansPanel storeId={store.id} tz={store.tz} />
      <table className="w-full overflow-hidden rounded-xl border bg-white text-sm">
        <thead className="bg-gray-50 text-left text-xs text-gray-500"><tr><th className="p-2">{t("staff.name")}</th><th className="p-2">{t("staff.status")}</th><th className="p-2">{t("staff.assigned")}</th><th className="p-2">{t("staff.shifts")}</th><th className="p-2 text-right">{t("staff.actions")}</th></tr></thead>
        <tbody>{active.map((s) => <Row key={s.id} s={s} />)}</tbody>
      </table>
      {inactive.length > 0 && (
        <div>
          <button type="button" className="text-sm text-gray-600 underline" onClick={() => setShowInactive(!showInactive)}>{showInactive ? "▾" : "▸"} {t("staff.archived", { n: inactive.length })}</button>
          {showInactive && <table className="mt-2 w-full overflow-hidden rounded-xl border bg-white text-sm"><tbody>{inactive.map((s) => <Row key={s.id} s={s} />)}</tbody></table>}
        </div>
      )}
      <Drawer open={!!editing} onClose={() => setEditing(null)} title={editing === "new" ? t("staff.new") : t("app.edit")}>
        {editing && <StaffForm storeId={store.id} staff={editing === "new" ? null : editing} services={services.data ?? []} onDone={() => { qc.invalidateQueries({ queryKey: ["staff"] }); qc.invalidateQueries({ queryKey: ["calendar"] }); setEditing(null); }} />}
      </Drawer>
    </div>
  );
}

function rowsFrom(shifts: Staff["shifts"]): DayRow[] {
  return DAYS.map((_, d) => { const sh = shifts.find((s) => s.weekday === d && !s.week_start && (s.parity ?? 0) === 0); return sh ? { on: true, start: sh.start_time.slice(0, 5), end: sh.end_time.slice(0, 5) } : { on: false, start: "09:00", end: "17:00" }; });
}

function StaffForm({ storeId, staff, services, onDone }: { storeId: string; staff: Staff | null; services: Service[]; onDone: () => void }) {
  const { t, i18n } = useTranslation();
  const [name, setName] = useState(staff?.display_name ?? "");
  const [color, setColor] = useState(staff?.color ?? "#0ea5e9");
  const [phone, setPhone] = useState(staff?.phone ?? "");
  const [active, setActive] = useState(staff?.active ?? true);
  const [serviceIds, setServiceIds] = useState<string[]>(staff?.service_ids ?? []);
  const [durations, setDurations] = useState<Record<string, string>>(Object.fromEntries(Object.entries(staff?.service_durations ?? {}).map(([k, v]) => [k, String(v)])));
  const [rows, setRows] = useState<DayRow[]>(rowsFrom(staff?.shifts ?? []));
  const [error, setError] = useState<string | null>(null);
  const shifts = () => rows.flatMap((r, d) => (r.on ? [{ weekday: d, start_time: `${r.start}:00`, end_time: `${r.end}:00` }] : []));
  const m = useMutation({
    mutationFn: async () => {
      const service_durations = Object.fromEntries(Object.entries(durations).filter(([k, v]) => serviceIds.includes(k) && v !== "" && Number(v) > 0).map(([k, v]) => [k, Number(v)]));
      const base = { display_name: name, color, phone: phone || null, active, service_ids: serviceIds };
      if (staff) {
        await api(`/app/staff/${staff.id}`, { method: "PATCH", body: JSON.stringify({ ...base, service_durations }) });
        const keepAB = staff.shifts.filter((s) => !s.week_start && (s.parity ?? 0) !== 0).map((s) => ({ weekday: s.weekday, start_time: s.start_time, end_time: s.end_time, parity: s.parity }));
        await api(`/app/staff/${staff.id}/shifts`, { method: "PUT", body: JSON.stringify([...shifts(), ...keepAB]) });
      } else {
        const created = await api<Staff>("/app/staff", { method: "POST", body: JSON.stringify({ store_id: storeId, ...base, shifts: shifts() }) });
        if (Object.keys(service_durations).length) await api(`/app/staff/${created.id}`, { method: "PATCH", body: JSON.stringify({ service_durations }) });
      }
    },
    onSuccess: onDone, onError: (e) => setError(e instanceof ApiError ? e.detail : t("app.error")),
  });
  const setRow = (d: number, patch: Partial<DayRow>) => setRows(rows.map((r, i) => (i === d ? { ...r, ...patch } : r)));
  return (
    <form onSubmit={(e: FormEvent) => { e.preventDefault(); m.mutate(); }} className="space-y-3 text-sm">
      <div className="grid grid-cols-2 gap-2">
        <Field label={t("staff.name")}><Input value={name} onChange={(e) => setName(e.target.value)} required /></Field>
        <Field label={t("staff.color")}><Input type="color" value={color} onChange={(e) => setColor(e.target.value)} /></Field>
        <Field label={t("customer.phone")}><Input value={phone} onChange={(e) => setPhone(e.target.value)} /></Field>
        <label className="flex items-center gap-2 self-end"><input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> {t("app.active")}</label>
      </div>
      <div>
        <div className="mb-1 font-medium">{t("staff.defaultShifts")}</div>
        <p className="mb-1 text-xs text-gray-500">{t("staff.defaultShiftsHint")}</p>
        <div className="space-y-1">{rows.map((r, d) => (
          <div key={d} className="grid grid-cols-[48px_auto_1fr_1fr] items-center gap-2">
            <span>{DAYS[d]}</span><input type="checkbox" checked={r.on} onChange={(e) => setRow(d, { on: e.target.checked })} />
            <Input type="time" value={r.start} disabled={!r.on} onChange={(e) => setRow(d, { start: e.target.value })} />
            <Input type="time" value={r.end} disabled={!r.on} onChange={(e) => setRow(d, { end: e.target.value })} />
          </div>))}</div>
      </div>
      <div>
        <div className="mb-1 font-medium">{t("nav.services")}</div>
        <p className="mb-1 text-xs text-gray-500">{t("staff.durationHint")}</p>
        <div className="space-y-1">{services.filter((s) => s.active).map((s) => {
          const on = serviceIds.includes(s.id);
          return (
            <div key={s.id} className="flex items-center gap-2">
              <label className="flex flex-1 items-center gap-1"><input type="checkbox" checked={on} onChange={(e) => setServiceIds(e.target.checked ? [...serviceIds, s.id] : serviceIds.filter((x) => x !== s.id))} /> {tx(s.name, i18n.language)} <span className="text-xs text-gray-400">({s.duration_min}′)</span></label>
              {on && <Input className="!w-24" type="number" min={5} max={480} step={5} placeholder={String(s.duration_min)} value={durations[s.id] ?? ""} onChange={(e) => setDurations({ ...durations, [s.id]: e.target.value })} />}
            </div>
          );
        })}</div>
      </div>
      {error && <p className="text-red-600">{error}</p>}
      <Button disabled={m.isPending}>{t("app.save")}</Button>
    </form>
  );
}
